import React, { useState } from 'react';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { StatusBadge } from 'components/StatusBadge';
import { formatDate, formatPrice, PARCEL_TYPES } from 'lib/format';
import { Package, MapPin, Calendar, ArrowRight, Star, Weight, Images } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from 'lib/i18n';

export const ParcelCard = ({ parcel, actionLabel, to }) => {
  const { t } = useLanguage();
  const link = to || `/deliveries/${parcel.id}`;
  const photos = Array.isArray(parcel.photos) ? parcel.photos.filter(photo => typeof photo === 'string' && photo.trim()) : [];
  const coverPhoto = photos[0];
  const [failedPhoto, setFailedPhoto] = useState(null);
  const showPhoto = coverPhoto && failedPhoto !== coverPhoto;
  const typeLabel = (PARCEL_TYPES.find(t => t.value === parcel.parcel_type) || {}).label || parcel.parcel_type;
  return (
    <Card className="min-w-0 transition-shadow duration-200 hover:shadow-md-custom hover:-translate-y-px overflow-hidden" data-testid={`parcel-card-${parcel.id}`}>
      <Link to={link} className="group relative block h-36 sm:h-40 overflow-hidden bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary" aria-label={`${t('card.viewParcel')} ${parcel.title}`} data-testid={`parcel-card-photo-link-${parcel.id}`}>
        {showPhoto ? (
          <img
            src={coverPhoto}
            alt={`${t('card.parcelPhoto')} ${parcel.title}`}
            width="640"
            height="360"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setFailedPhoto(coverPhoto)}
            className="h-full w-full object-cover object-center transition-transform duration-300 motion-safe:group-hover:scale-105"
            data-testid={`parcel-card-photo-${parcel.id}`}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-secondary to-muted text-muted-foreground" data-testid={`parcel-card-photo-fallback-${parcel.id}`}>
            <Package size={36} strokeWidth={1.5} aria-hidden="true" />
            <span className="text-xs">{t('card.noParcelPhoto')}</span>
          </div>
        )}
        {showPhoto && photos.length > 1 && (
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/65 px-2 py-1 text-xs text-white">
            <Images size={12} aria-hidden="true" />{photos.length} {t('card.photos')}
          </span>
        )}
      </Link>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary shrink-0">
              <Package size={16} className="text-foreground/70" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{parcel.title}</div>
              <div className="text-xs text-muted-foreground">{typeLabel} • {parcel.size}</div>
            </div>
          </div>
          <StatusBadge status={parcel.status} />
        </div>

        <div className="flex items-center gap-2 text-sm mb-2">
          <MapPin size={14} className="text-primary shrink-0" />
          <span className="truncate">{parcel.departure_city}, {parcel.departure_country}</span>
          <ArrowRight size={14} className="text-muted-foreground shrink-0" />
          <span className="truncate">{parcel.arrival_city}, {parcel.arrival_country}</span>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Weight size={12} /> {parcel.weight_kg} kg</span>
            {parcel.desired_date && <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(parcel.desired_date)}</span>}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-secondary">
                {(parcel.sender_name || 'U').split(' ').map(n => n[0]).slice(0, 2).join('')}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground">{parcel.sender_name || t('card.anonymous')}</span>
            {parcel.sender_rating > 0 && (
              <span className="flex items-center gap-0.5 text-xs">
                <Star size={10} className="fill-amber-500 text-amber-500" /> {parcel.sender_rating.toFixed(1)}
              </span>
            )}
          </div>
          <span className="shrink-0 font-semibold text-primary">{formatPrice(parcel.budget)}</span>
        </div>
        <Button asChild className="mt-4 min-h-11 w-full justify-between gap-3 rounded-lg px-4 text-sm font-semibold">
          <Link to={link} data-testid={`parcel-card-open-button-${parcel.id}`}>
            <span>{actionLabel || t('card.viewParcel')}</span>
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
